import { useState, useRef, useCallback } from "react";
import { API } from "../App";
import axios from "axios";

const useConsultation = ({ lang, t }) => {
  const [phase, setPhase] = useState("input");
  const [dilemma, setDilemma] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [questionNumber, setQuestionNumber] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(5);
  const [answer, setAnswer] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const inputRef = useRef(null);

  const addChat = useCallback((role, text) => {
    setChatHistory(prev => [...prev, { role, text, id: Date.now() + Math.random() }]);
  }, []);

  const focusInput = useCallback(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const handleStartConsultation = useCallback(async (text) => {
    const q = text || dilemma;
    if (!q.trim()) return;
    setDilemma(q);
    setLoading(true);
    setError("");
    setChatHistory([]);
    addChat("user", q);

    try {
      const res = await axios.post(`${API}/futura/start`, { question: q.trim(), language: lang }, { withCredentials: true, timeout: 60000 });
      setSessionId(res.data.session_id);
      setCurrentQuestion(res.data.question);
      setQuestionNumber(res.data.question_number);
      setTotalQuestions(res.data.total_questions);
      addChat("ai", res.data.question);
      setPhase("questions");
    } catch (err) {
      if (err.response?.status === 403) {
        setError(t("result_limit_reached"));
      } else {
        setError(err.response?.data?.detail || "Error");
      }
    } finally {
      setLoading(false);
      focusInput();
    }
  }, [dilemma, lang, t, addChat, focusInput]);

  const handleSubmitAnswer = useCallback(async () => {
    if (!answer.trim() || loading) return;
    const answerText = answer.trim();
    setAnswer("");
    setLoading(true);
    addChat("user", answerText);

    try {
      const res = await axios.post(`${API}/futura/answer`, {
        session_id: sessionId, answer: answerText, language: lang
      }, { withCredentials: true, timeout: 60000 });

      if (res.data.done) {
        setPhase("loading_result");
        addChat("ai", t("consulta_generating"));
        setTimeout(() => { setResponse(res.data.result); setPhase("result"); }, 1500);
      } else {
        setCurrentQuestion(res.data.question);
        setQuestionNumber(res.data.question_number);
        setTotalQuestions(res.data.total_questions);
        addChat("ai", res.data.question);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
      focusInput();
    }
  }, [answer, loading, sessionId, lang, t, addChat, focusInput]);

  const handleReset = useCallback(() => {
    setPhase("input");
    setDilemma("");
    setSessionId(null);
    setCurrentQuestion("");
    setAnswer("");
    setChatHistory([]);
    setResponse(null);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return {
    phase, dilemma, setDilemma, currentQuestion, questionNumber, totalQuestions,
    answer, setAnswer, chatHistory, response, loading, error,
    inputRef, handleStartConsultation, handleSubmitAnswer, handleReset,
  };
};

export default useConsultation;
